/**
 * The Languages tab — the site's home (wireframes 3a / 5a / 5b).
 *
 * The panel at the top is the onboarding checklist while setup is unfinished,
 * and turns into the site status panel once everything is connected. The work
 * itself happens in the language rows: toggle-free for now (per-language
 * on/off and pause are phase 3), each row is a status plus one obvious action.
 *
 * The two modes differ in exactly one place:
 *   subdomain (5a) — records live *in the row*, one set per language, and each
 *                    language goes live on its own as its records appear;
 *   subfolder (5b) — one set of records for the whole site, in the top panel,
 *                    and every language goes live together.
 */

import { useEffect, useState } from 'react'

import { LANGUAGE_LIST, nameFor, slugFor, type Lang } from '@glocal/lang'
import type { ILang, IRoute, IWebsite } from '@glocal/schemas'

import { Chip } from '../../components/chip'
import { CopyButton } from '../../components/copy-button'
import { MiniButton } from '../../components/mini-button'
import { OverflowMenu, type MenuItem } from '../../components/overflow-menu'
import { formatRecords, RecordLine, type DnsRecordLine } from '../../components/record-line'
import { trpc } from '../../trpc'

import {
	domainReady,
	isLive,
	recordLinesFor,
	siteSlots,
	type CustomHostnameDetails,
	type DomainStatusMap,
} from './domain-status'

/**
 * Free plan connects ONE language to the real domain (decision 27.07); other
 * languages stay live as demo previews. Languages that already have a custom
 * hostname are grandfathered (never locked).
 */
const FREE_CONNECT_LIMIT = 1

type RowStatus = 'live' | 'waiting' | 'preview' | 'registering' | 'gone'

interface RouteIndex {
	customByLang: Map<string, IRoute>
	demoByLang: Map<string, IRoute>
	/**
	 * Self-service subfolder points the apex at us via one custom hostname (no
	 * `lang`). Its absence means an external-CDN setup (mooz) where the
	 * customer's CDN handles routing/SSL and there is nothing to verify.
	 */
	apexRoute?: IRoute
}

function indexRoutes(routes: IRoute[]): RouteIndex {
	const customByLang = new Map<string, IRoute>()
	const demoByLang = new Map<string, IRoute>()
	for (const r of routes) {
		if (r.redirect) {
			continue
		}
		if (r.lang && r.custom_hostname_id) {
			customByLang.set(r.lang, r)
		}
		if (r.lang && !r.custom_hostname_id && r.hostname.endsWith('.nayda.pro')) {
			demoByLang.set(r.lang, r)
		}
	}
	const apexRoute = routes.find((r) => r.custom_hostname_id && !r.lang && !r.redirect)
	return { customByLang, demoByLang, apexRoute }
}

/**
 * Routes whose hostname doesn't match the expected `${lang}.${origin}` pattern
 * — happens after the user edits the Site address. One button refreshes every
 * address at once instead of editing each language manually.
 */
function findStale(website: IWebsite, customByLang: Map<string, IRoute>) {
	const stale: { lang: string; route: IRoute; expected: string }[] = []
	if (website.mode === 'subfolder') {
		return stale
	}
	for (const lang of website.available_languages) {
		const route = customByLang.get(lang)
		const expected = suggestedHostname(siteAddress(website), lang)
		if (route && expected && route.hostname !== expected) {
			stale.push({ lang, route, expected })
		}
	}
	return stale
}

export function LanguagesTab({
	website,
	routes,
	statuses,
	refresh,
	onChanged,
}: {
	website: IWebsite
	routes: IRoute[]
	statuses: DomainStatusMap
	refresh: () => Promise<void>
	onChanged: () => void
}) {
	const [adding, setAdding] = useState(false)
	const [pending, setPending] = useState<string | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [updatingAll, setUpdatingAll] = useState(false)

	const isSubfolder = website.mode === 'subfolder'
	const { customByLang, demoByLang, apexRoute } = indexRoutes(routes)
	const stale = findStale(website, customByLang)

	// Whether the domain is pointed at us — a question about DNS only, shared
	// with the header chip via the same helper.
	const slots = siteSlots(website, routes)
	const connectedCount = slots.filter((r) => isLive(statuses[r.custom_hostname_id as string])).length
	const ready = domainReady(website, routes, statuses)

	const liveCount = isSubfolder ? (ready ? website.available_languages.length : 0) : connectedCount

	async function updateStaleDomains() {
		if (stale.length === 0 || updatingAll) {
			return
		}
		setUpdatingAll(true)
		setError(null)
		try {
			for (const { lang, route, expected } of stale) {
				try {
					if (route.custom_hostname_id) {
						await trpc().CloudflareService.DeleteCustomHostname.mutate(route.custom_hostname_id)
					}
				} catch {
					/* ignore — CF may already have dropped it */
				}
				await trpc().RouteService.Delete.mutate(route.hostname)
				const newId = await trpc().CloudflareService.CreateCustomHostname.mutate({
					website_id: website.id,
					hostname: expected,
				})
				await trpc().RouteService.Create.mutate({
					website_id: website.id,
					hostname: expected,
					lang: lang as ILang,
					custom_hostname_id: newId,
				})
			}
			onChanged()
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Failed to update some domains')
		} finally {
			setUpdatingAll(false)
		}
	}

	async function setLangs(next: ILang[]) {
		setError(null)
		try {
			await trpc().WebsiteService.Update.mutate({
				id: website.id,
				available_languages: next,
			})
			onChanged()
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Failed to update languages')
		}
	}

	async function remove(lang: ILang) {
		setPending(lang)
		const route = customByLang.get(lang)
		if (route) {
			try {
				if (route.custom_hostname_id) {
					await trpc().CloudflareService.DeleteCustomHostname.mutate(route.custom_hostname_id)
				}
				await trpc().RouteService.Delete.mutate(route.hostname)
			} catch (e) {
				setError(
					`Couldn't fully remove ${langLabel(lang)} — please try again. ` +
						(e instanceof Error ? e.message : '')
				)
				setPending(null)
				return
			}
		}
		await setLangs(website.available_languages.filter((l) => l !== lang))
		setPending(null)
	}

	async function add(lang: ILang) {
		setPending(lang)
		await setLangs([...website.available_languages, lang])
		setPending(null)
		setAdding(false)
	}

	/**
	 * Every picked language has a free preview on *.nayda.pro — the wizard
	 * creates the route up front. Sites that predate that get it created here
	 * on first use, best-effort, while the tab opens immediately.
	 */
	function openPreview(lang: string) {
		const hostname = demoByLang.get(lang)?.hostname ?? previewAddress(website, lang)
		window.open(`https://${hostname}`, '_blank', 'noopener')
		if (!demoByLang.has(lang)) {
			void trpc()
				.RouteService.Create.mutate({ website_id: website.id, hostname, lang: lang as ILang })
				.then(onChanged)
				.catch(() => {
					/* best-effort — the preview link itself already opened */
				})
		}
	}

	return (
		<div className="flex flex-col gap-4">
			<TopPanel
				website={website}
				apexRoute={apexRoute}
				statuses={statuses}
				ready={ready}
				connected={connectedCount}
				total={slots.length}
				liveCount={liveCount}
				refresh={refresh}
			/>

			{stale.length > 0 && (
				<div className="flex items-center justify-between gap-4 rounded-nayda-sm border border-marigold/30 bg-marigold/15 px-4 py-3">
					<div className="font-sans text-[14px] text-ink">
						Site address changed. Refresh the language addresses to match the new domain.
					</div>
					<button
						onClick={() => void updateStaleDomains()}
						disabled={updatingAll}
						className="btn btn-accent h-9 shrink-0 px-4 text-[13px]"
					>
						{updatingAll ? 'Updating…' : 'Update addresses'}
					</button>
				</div>
			)}

			{/* No `overflow-hidden` here: the ⋯ menu on the last rows has to be able
			    to escape the card. Rows carry no background of their own, so the
			    rounded corners survive without clipping. */}
			{website.available_languages.length === 0 ? (
				<div className="card p-5 text-[14px] text-ink-soft">
					No target languages yet. Add one to start translating.
				</div>
			) : (
				<div className="card">
					<ul className="divide-y divide-line">
						{website.available_languages.map((lang) => (
							<li key={lang}>
								<LangRow
									website={website}
									lang={lang}
									route={customByLang.get(lang)}
									apexRoute={apexRoute}
									locked={!customByLang.has(lang) && customByLang.size >= FREE_CONNECT_LIMIT}
									statuses={statuses}
									removing={pending === lang}
									onRemove={() => void remove(lang)}
									onPreview={() => openPreview(lang)}
									onChanged={onChanged}
								/>
							</li>
						))}
					</ul>
				</div>
			)}

			{adding && (
				<div className="card p-4">
					<LangSearchPicker
						website={website}
						onPick={(lang) => void add(lang)}
					/>
				</div>
			)}
			<button
				onClick={() => setAdding((v) => !v)}
				className="self-start font-sans text-[13px] text-ink-soft transition-colors hover:text-ink"
			>
				{adding ? 'Cancel' : '+ Add a language'}
			</button>

			{error && (
				<div className="rounded-nayda-sm border border-[#B23B2A]/20 bg-[#B23B2A]/10 px-3 py-2 text-[13px] text-[#B23B2A]">
					{error}
				</div>
			)}
		</div>
	)
}

/* ============= Top panel ============= */

function TopPanel({
	website,
	apexRoute,
	statuses,
	ready,
	connected,
	total,
	liveCount,
	refresh,
}: {
	website: IWebsite
	apexRoute?: IRoute
	statuses: DomainStatusMap
	ready: boolean
	connected: number
	total: number
	liveCount: number
	refresh: () => Promise<void>
}) {
	if (ready) {
		return (
			<StatusPanel
				liveCount={liveCount}
				refresh={refresh}
			/>
		)
	}
	if (website.mode === 'subfolder' && apexRoute) {
		return (
			<SubfolderOnboardingPanel
				host={hostOf(website)}
				records={recordLinesFor(apexRoute.hostname, statuses[apexRoute.custom_hostname_id as string])}
			/>
		)
	}
	return (
		<SubdomainOnboardingPanel
			connected={connected}
			total={total}
		/>
	)
}

function PanelShell({ children, tone = 'work' }: { children: React.ReactNode; tone?: 'work' | 'calm' }) {
	// The onboarding panel is white with only a marigold border — the accent
	// stays a rare event, per the brand's tone.
	return (
		<section
			className={
				'rounded-nayda-lg border p-5 ' + (tone === 'work' ? 'border-marigold bg-paper' : 'border-line bg-paper')
			}
		>
			{children}
		</section>
	)
}

function Checklist({ addresses }: { addresses: string }) {
	return (
		<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
			<span className="text-ink-soft">✓ Site added</span>
			<span className="text-ink-mute">·</span>
			<span className="text-ink-soft">✓ Languages picked</span>
			<span className="text-ink-mute">·</span>
			<span className="font-medium text-ember">○ {addresses}</span>
			<span className="text-ink-mute">·</span>
			<span className="text-ink-mute">○ Live 🎉</span>
		</div>
	)
}

function CheckingHint() {
	return <span className="text-[12px] text-ink-soft">◐ checking every few minutes</span>
}

function SubdomainOnboardingPanel({ connected, total }: { connected: number; total: number }) {
	// Language addresses register right after the wizard, so a site can land
	// here before any exist — that's the same "almost there", not an error.
	return (
		<PanelShell>
			<div className="flex flex-wrap items-baseline justify-between gap-2">
				<h2 className="font-sans text-[16px] font-medium text-ink">
					{total === 0
						? 'Almost there — setting up your language addresses'
						: `Almost there — ${connected} of ${total} ${total === 1 ? 'language' : 'languages'} connected`}
				</h2>
				<CheckingHint />
			</div>
			<div className="mt-3">
				<Checklist addresses={`Addresses pointed at Nayda (${connected}/${total})`} />
			</div>
			<p className="mt-3 text-[12px] text-ink-soft">
				Records for each waiting language are in its row below ↓ Once everything connects, this panel becomes
				the site status panel.
			</p>
		</PanelShell>
	)
}

function SubfolderOnboardingPanel({ host, records }: { host: string; records: DnsRecordLine[] }) {
	const found = records.filter((r) => r.found).length
	const missing = records.filter((r) => !r.found)
	return (
		<PanelShell>
			<div className="flex flex-wrap items-baseline justify-between gap-2">
				<h2 className="font-sans text-[16px] font-medium text-ink">One thing left: point your domain at us</h2>
				<CheckingHint />
			</div>
			<p className="mt-2 text-[13px] text-ink-soft">
				Folder addresses like <span className="font-mono text-ink">{host}/es/</span> connect the whole site at
				once — add {records.length === 1 ? 'this record' : `these ${records.length} records`} where you bought
				your domain:
			</p>

			<div className="mt-3 flex flex-col gap-2">
				{records.map((r) => (
					<RecordLine
						key={r.name + r.kind}
						{...r}
					/>
				))}
			</div>

			<div className="mt-4 flex flex-wrap items-center justify-between gap-2">
				<Checklist addresses={`Domain pointed (${found} of ${records.length} records)`} />
				{missing.length > 0 && (
					<CopyButton
						value={formatRecords(missing)}
						label="Copy all missing"
					/>
				)}
			</div>
			<p className="mt-2 text-[12px] text-ink-soft">
				Once connected, all languages go live together and this panel becomes the site status panel.
			</p>
		</PanelShell>
	)
}

/**
 * The same slot once the domain is connected — the site status panel. The
 * master translations switch joins it in phase 3; until then it answers the
 * one question left: is everything still working?
 */
function StatusPanel({ liveCount, refresh }: { liveCount: number; refresh: () => Promise<void> }) {
	const [checking, setChecking] = useState(false)

	async function checkConnection() {
		setChecking(true)
		try {
			await refresh()
		} finally {
			window.setTimeout(() => setChecking(false), 600)
		}
	}

	return (
		<PanelShell tone="calm">
			<div className="flex flex-wrap items-baseline gap-x-2">
				<h2 className="font-sans text-[16px] font-medium text-ink">
					{checking ? 'Checking your site…' : "Everything's working"}
				</h2>
				<span className="text-[12px] text-ink-soft">
					{checking
						? 'asking Cloudflare for the latest status'
						: `${liveCount} ${liveCount === 1 ? 'language' : 'languages'} live · checking every few minutes`}
				</span>
			</div>
			<div className="mt-3 flex flex-wrap items-center gap-2">
				<MiniButton
					onClick={() => void checkConnection()}
					disabled={checking}
				>
					{checking ? 'Checking…' : 'Check connection'}
				</MiniButton>
				<span className="text-[12px] text-ink-mute">use this if something seems off</span>
			</div>
		</PanelShell>
	)
}

/* ============= Language row ============= */

interface RowContext {
	website: IWebsite
	lang: string
	route?: IRoute
	apexRoute?: IRoute
	locked: boolean
	details: CustomHostnameDetails | null | undefined
	statuses: DomainStatusMap
}

function rowStatusOf(ctx: RowContext): RowStatus {
	if (ctx.website.mode === 'subfolder') {
		if (!ctx.apexRoute) {
			// External-CDN setup — nothing on our side to verify.
			return 'live'
		}
		return isLive(ctx.statuses[ctx.apexRoute.custom_hostname_id as string]) ? 'live' : 'waiting'
	}
	if (ctx.locked) {
		return 'preview'
	}
	if (!ctx.route) {
		return 'registering'
	}
	if (ctx.details === null) {
		return 'gone'
	}
	return isLive(ctx.details) ? 'live' : 'waiting'
}

function rowAddressOf(ctx: RowContext, status: RowStatus): string {
	if (ctx.website.mode === 'subfolder') {
		return subfolderAddress(ctx.website, ctx.lang)
	}
	if (status === 'preview') {
		return previewAddress(ctx.website, ctx.lang)
	}
	return ctx.route?.hostname ?? suggestedHostname(siteAddress(ctx.website), ctx.lang)
}

function buildMenuItems({
	ctx,
	status,
	missing,
	removing,
	onPreview,
	onReissue,
	onRemove,
}: {
	ctx: RowContext
	status: RowStatus
	missing: DnsRecordLine[]
	removing: boolean
	onPreview: () => void
	onReissue: () => void
	onRemove: () => void
}): MenuItem[] {
	const items: MenuItem[] = [
		{
			label: 'Translation preview ↗',
			hint: previewAddress(ctx.website, ctx.lang),
			onSelect: onPreview,
		},
	]
	if (missing.length > 0) {
		items.push({
			label: 'Copy all missing records',
			hint: `${missing.length} still to add`,
			onSelect: () => void navigator.clipboard?.writeText(formatRecords(missing)),
		})
	}
	if (ctx.website.mode === 'subdomain' && ctx.route && status !== 'live') {
		items.push({
			label: 'Re-issue domain records',
			hint: 'if something seems off',
			onSelect: onReissue,
		})
	}
	const isSource = ctx.website.mode === 'subfolder' && ctx.lang === ctx.website.lang
	if (!isSource) {
		items.push(removeMenuItem(ctx.lang, status, removing, onRemove))
	}
	return items
}

/** "Remove language" asks first only when the language is actually serving. */
function removeMenuItem(lang: string, status: RowStatus, removing: boolean, onRemove: () => void): MenuItem {
	const live = status === 'live'
	return {
		label: removing ? 'Removing…' : 'Remove language',
		hint: live
			? 'takes it off your domain — visitors get a 404'
			: 'stops setup, frees the slot; the preview link dies',
		danger: true,
		onSelect: () => {
			const question = live
				? `Remove ${langLabel(lang)}? It's live now — visitors will get a 404.`
				: `Remove ${langLabel(lang)}? Its address and preview link stop working.`
			if (!removing && confirm(question)) {
				onRemove()
			}
		},
	}
}

/**
 * One language: name · address · status chip · one action button · "⋯" menu.
 * The chip is never clickable — the action is always its own button.
 */
function LangRow({
	website,
	lang,
	route,
	apexRoute,
	locked,
	statuses,
	removing,
	onRemove,
	onPreview,
	onChanged,
}: {
	website: IWebsite
	lang: string
	route?: IRoute
	apexRoute?: IRoute
	locked: boolean
	statuses: DomainStatusMap
	removing: boolean
	onRemove: () => void
	onPreview: () => void
	onChanged: () => void
}) {
	const details = route?.custom_hostname_id ? statuses[route.custom_hostname_id] : undefined
	const ctx: RowContext = { website, lang, route, apexRoute, locked, details, statuses }

	const { registerError } = useAutoRegister(ctx, onChanged)
	const [reissuing, setReissuing] = useState(false)

	async function reissue() {
		if (!route?.lang) {
			return
		}
		setReissuing(true)
		try {
			await trpc().RouteService.Reconcile.mutate({
				website_id: route.website_id,
				hostname: route.hostname,
				lang: route.lang,
			})
			onChanged()
		} catch {
			/* reconciliation failed — the owner can retry */
		} finally {
			setReissuing(false)
		}
	}

	const status = rowStatusOf(ctx)
	const address = rowAddressOf(ctx, status)

	// Records only ever hang off a row in subdomain mode — in folder mode the
	// one set at the top covers the whole site, so the row has nothing to expand.
	const records =
		website.mode === 'subdomain' && route && status === 'waiting' ? recordLinesFor(route.hostname, details) : []
	const missing = records.filter((r) => !r.found)
	const [open, setOpen] = useState(true)

	const menuItems = buildMenuItems({
		ctx,
		status,
		missing,
		removing,
		onPreview,
		onReissue: () => void reissue(),
		onRemove,
	})

	return (
		<div className="px-4 py-3">
			<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
				<span className="font-sans text-[15px] font-medium text-ink">{langLabel(lang)}</span>
				<span className="truncate font-mono text-[12px] text-ink">{address}</span>

				<div className="ml-auto flex flex-wrap items-center gap-2">
					<StatusChip
						status={status}
						isSubfolder={website.mode === 'subfolder'}
						missing={missing.length}
						pending={details === undefined && status === 'waiting'}
					/>

					<RowAction
						status={status}
						address={address}
						reissuing={reissuing}
						onPreview={onPreview}
						onReissue={() => void reissue()}
					/>

					{missing.length > 0 && (
						<button
							type="button"
							onClick={() => setOpen((v) => !v)}
							aria-expanded={open}
							className="h-8 rounded-nayda-sm px-2 text-[12px] text-ink-soft transition-colors hover:bg-surface hover:text-ink"
						>
							{open
								? 'Hide records ▴'
								: `Show ${missing.length} ${missing.length === 1 ? 'record' : 'records'} ▾`}
						</button>
					)}

					<OverflowMenu
						items={menuItems}
						label={`More actions for ${langLabel(lang)}`}
					/>
				</div>
			</div>

			<RowHint
				status={status}
				registerError={registerError}
			/>

			{missing.length > 0 && open && (
				<RowRecords
					records={records}
					missing={missing}
				/>
			)}
		</div>
	)
}

/**
 * Auto-register the language address on first view: subdomain mode only, and
 * never for locked languages — on the free plan those exist as demo previews
 * until upgrade. Subfolder mode serves languages at /<slug>/ on the customer's
 * own domain, so there is nothing to register.
 */
function useAutoRegister(ctx: RowContext, onChanged: () => void): { registerError: boolean } {
	const { website, lang, route, locked } = ctx
	const isSubfolder = website.mode === 'subfolder'
	const suggested = suggestedHostname(siteAddress(website), lang)
	const [registering, setRegistering] = useState(false)
	const [registerError, setRegisterError] = useState(false)

	const skip = isSubfolder || locked || !!route || registering

	useEffect(() => {
		if (skip) {
			return
		}
		let cancelled = false
		void (async () => {
			setRegistering(true)
			setRegisterError(false)
			try {
				const id = await trpc().CloudflareService.CreateCustomHostname.mutate({
					website_id: website.id,
					hostname: suggested,
				})
				await trpc().RouteService.Create.mutate({
					website_id: website.id,
					hostname: suggested,
					lang: lang as ILang,
					custom_hostname_id: id,
				})
				if (!cancelled) {
					onChanged()
				}
			} catch {
				if (!cancelled) {
					setRegisterError(true)
				}
			} finally {
				if (!cancelled) {
					setRegistering(false)
				}
			}
		})()
		return () => {
			cancelled = true
		}
		// `skip` folds every "don't touch it" condition into one dependency, so a
		// revalidation that delivers the new route stops the loop naturally.
	}, [skip, suggested, lang, website.id])
	return { registerError }
}

function RowAction({
	status,
	address,
	reissuing,
	onPreview,
	onReissue,
}: {
	status: RowStatus
	address: string
	reissuing: boolean
	onPreview: () => void
	onReissue: () => void
}) {
	if (status === 'live') {
		return (
			<MiniButton
				onClick={() => window.open(`https://${address}`, '_blank', 'noopener')}
				title={`Open ${address}`}
			>
				Open ↗
			</MiniButton>
		)
	}
	if (status === 'waiting' || status === 'preview') {
		return <MiniButton onClick={onPreview}>Translation preview ↗</MiniButton>
	}
	if (status === 'gone') {
		return (
			<MiniButton
				onClick={onReissue}
				disabled={reissuing}
			>
				{reissuing ? 'Re-issuing…' : 'Re-issue records'}
			</MiniButton>
		)
	}
	return null
}

function RowHint({ status, registerError }: { status: RowStatus; registerError: boolean }) {
	if (status === 'registering') {
		return (
			<p className="mt-1.5 text-[12px] text-ink-soft">
				{registerError
					? 'Failed to set up. Try removing and re-adding the language.'
					: '✨ Setting things up — usually 1–2 minutes'}
			</p>
		)
	}
	if (status === 'preview') {
		return (
			<p className="mt-1.5 text-[12px] text-ink-soft">
				Free plan connects one language to your domain — this one stays a free preview.
			</p>
		)
	}
	if (status === 'gone') {
		return (
			<p className="mt-1.5 text-[12px] text-ink-soft">
				This address expired on our side — re-issue to get fresh records.
			</p>
		)
	}
	return null
}

/** The dashed record block that expands under a waiting row (5a). */
function RowRecords({ records, missing }: { records: DnsRecordLine[]; missing: DnsRecordLine[] }) {
	return (
		// Indented under the row on desktop so it reads as belonging to that
		// language; full-width on a phone, where the UUIDs need every pixel.
		<div className="mt-3 flex flex-col gap-2 rounded-nayda border border-dashed border-line-strong bg-surface/40 p-3 sm:ml-6">
			{records.map((r) => (
				<RecordLine
					key={r.name + r.kind}
					{...r}
				/>
			))}
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="text-[11px] text-ink-mute">
					Sometimes 1 TXT record, sometimes 2 — only what's missing is shown.
				</span>
				{missing.length > 0 && (
					<CopyButton
						value={formatRecords(missing)}
						label="Copy all missing"
					/>
				)}
			</div>
		</div>
	)
}

function StatusChip({
	status,
	isSubfolder,
	missing,
	pending,
}: {
	status: RowStatus
	isSubfolder: boolean
	missing: number
	pending: boolean
}) {
	if (status === 'live') {
		return <Chip tone="live">● Live</Chip>
	}
	if (status === 'preview') {
		return <Chip tone="preview">▶ Preview only</Chip>
	}
	if (status === 'registering') {
		return <Chip tone="waiting">◐ Setting up</Chip>
	}
	if (status === 'gone') {
		return <Chip tone="waiting">◐ Expired</Chip>
	}
	// Waiting. In folder mode every waiting row points at the one top panel, so
	// naming a record count in the row would be misleading.
	if (isSubfolder) {
		return <Chip tone="waiting">◐ Waiting for your domain connection</Chip>
	}
	if (pending || missing === 0) {
		return <Chip tone="waiting">◐ Checking</Chip>
	}
	return (
		<Chip tone="waiting">
			◐ Waiting for {missing} {missing === 1 ? 'record' : 'records'}
		</Chip>
	)
}

/* ============= Helpers ============= */

// Display names come from our own language registry (the single table), not the
// browser's Intl database — Intl labels both fil and tl as "Filipino"
// (ambiguous) and we want control over edge cases.
export function langLabel(code: string): string {
	return nameFor(code as Lang) ?? code.toUpperCase()
}

/**
 * The address visitors use. Differs from `origin` only for a folder site we
 * fetch from a hidden origin — everything the customer sees (language
 * addresses, previews) is built from this, never from where we fetch.
 */
export function siteAddress(website: IWebsite): string {
	return website.public ?? website.origin
}

export function hostOf(website: IWebsite): string {
	try {
		return new URL(siteAddress(website)).hostname.replace(/^www\./, '')
	} catch {
		return website.origin.replace(/^https?:\/\//, '')
	}
}

/** The slug the wizard names demo routes with — `www.` dropped first, as it does. */
function originSlug(originUrl: string): string {
	try {
		return new URL(originUrl).hostname
			.replace(/^www\./, '')
			.replace(/\W+/g, '-')
			.toLowerCase()
	} catch {
		return ''
	}
}

/**
 * Build the expected hostname for the language. We strip any leading "www." so
 * suggestions for `https://www.foo.com` come out as `es.foo.com`. Always the
 * language's URL slug (lowercase), not the raw code — a Title-case code like
 * `zh-Hant` would make an invalid/mismatched host.
 */
export function suggestedHostname(originUrl: string, lang: string): string {
	try {
		const host = new URL(originUrl).hostname.replace(/^www\./, '')
		return `${slugFor(lang as Lang)}.${host}`
	} catch {
		return ''
	}
}

/** The free *.nayda.pro preview every picked language gets right away. */
function previewAddress(website: IWebsite, lang: string): string {
	return `${slugFor(lang as Lang)}-${originSlug(siteAddress(website))}.nayda.pro`
}

function subfolderAddress(website: IWebsite, lang: string): string {
	const base = siteAddress(website)
		.replace(/\/+$/, '')
		.replace(/^https?:\/\//, '')
	return lang === website.lang ? base : `${base}/${slugFor(lang as Lang)}/`
}

function LangSearchPicker({ website, onPick }: { website: IWebsite; onPick: (l: ILang) => void }) {
	const [search, setSearch] = useState('')

	const available = (LANGUAGE_LIST as readonly string[])
		.filter((l) => l !== website.lang && !website.available_languages.includes(l as ILang))
		.map((l) => ({ code: l as ILang, label: langLabel(l) }))
		.sort((a, b) => a.label.localeCompare(b.label))

	const filtered = search
		? available.filter(
				(l) =>
					l.label.toLowerCase().includes(search.toLowerCase()) ||
					l.code.toLowerCase().includes(search.toLowerCase())
			)
		: available

	return (
		<div>
			<input
				type="text"
				autoFocus
				placeholder="Search a language…"
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				className="field mb-3"
			/>
			<div className="max-h-[260px] overflow-y-auto -mx-1 px-1">
				{filtered.length === 0 ? (
					<div className="px-3 py-6 text-center text-[13px] text-ink-mute">No matches.</div>
				) : (
					<ul className="flex flex-col">
						{filtered.map((l) => (
							<li key={l.code}>
								<button
									type="button"
									onClick={() => onPick(l.code)}
									className="flex w-full items-center justify-between rounded-nayda-sm px-3 py-2 text-left transition-colors hover:bg-surface"
								>
									<span className="text-[14px] text-ink">{l.label}</span>
									<span className="font-mono text-[11px] uppercase text-ink-mute">{l.code}</span>
								</button>
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	)
}
